import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UmrahMandiriPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ ppn_enabled: false, ppn_rate: 1.0 });

  // Master Data
  const [airlines, setAirlines] = useState([]);
  const [visas, setVisas] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [transports, setTransports] = useState([]);
  const [muthowifs, setMuthowifs] = useState([]);

  // Toggle ON/OFF per kategori — true = wajib pilih & tampil
  const [toggles, setToggles] = useState({
    airline: true,
    visa: true,
    hotel: true,
    transport: true,
    muthowif: true,
  });

  // Selected Data
  const [selections, setSelections] = useState({
    departure_date: '',
    return_date: '',
    total_pilgrims: 1,
    departure_city: 'Jakarta',
    airline_id: null,
    visa_id: null,
    hotel_id: null,
    transport_id: null,
    muthowif_id: null
  });

  // Fetch master data
  useEffect(() => {
    fetchMasterData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/`);
      setSettings({ ppn_enabled: res.data.ppn_enabled ?? false, ppn_rate: res.data.ppn_rate ?? 1.0 });
    } catch (e) { /* ignore */ }
  };

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const [airlinesRes, visasRes, hotelsRes, transportsRes, muthowifsRes] = await Promise.all([
        axios.get(`${API}/master/airlines`),
        axios.get(`${API}/master/visas`),
        axios.get(`${API}/hotels/`),
        axios.get(`${API}/master/transportations`),
        axios.get(`${API}/master/muthowifs`)
      ]);

      setAirlines(airlinesRes.data);
      setVisas(visasRes.data);
      setHotels(hotelsRes.data);
      setTransports(transportsRes.data);
      setMuthowifs(muthowifsRes.data);
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const toggleStep = (key) => {
    setToggles(prev => {
      const newVal = !prev[key];
      // Kalau toggle OFF, hapus seleksi kategori itu
      if (!newVal) {
        const fieldMap = { airline: 'airline_id', visa: 'visa_id', hotel: 'hotel_id', transport: 'transport_id', muthowif: 'muthowif_id' };
        setSelections(s => ({ ...s, [fieldMap[key]]: null }));
      }
      return { ...prev, [key]: newVal };
    });
  };

  const steps = [
    { number: 1, title: 'Tanggal & Jamaah', description: 'Pilih tanggal dan jumlah jamaah' },
    { number: 2, title: 'Maskapai', description: 'Pilih maskapai penerbangan' },
    { number: 3, title: 'Visa', description: 'Pilih jenis visa' },
    { number: 4, title: 'Hotel', description: 'Pilih hotel di Makkah' },
    { number: 5, title: 'Transportasi', description: 'Pilih kendaraan' },
    { number: 6, title: 'Muthowif', description: 'Pilih pembimbing' },
    { number: 7, title: 'Review', description: 'Tinjau pilihan Anda' }
  ];

  const handleNext = () => {
    if (currentStep === 1) {
      if (!selections.departure_date || !selections.return_date || selections.total_pilgrims < 1) {
        toast.error('Lengkapi tanggal berangkat, pulang, dan jumlah jamaah');
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, steps.length));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const calculateTotal = () => {
    let total = 0;
    const pilgrims = selections.total_pilgrims;
    const nights = selections.departure_date && selections.return_date
      ? Math.max(1, Math.round((new Date(selections.return_date) - new Date(selections.departure_date)) / 86400000))
      : 12;

    if (toggles.airline && selections.airline_id) {
      const airline = airlines.find(a => a.id === selections.airline_id);
      total += (airline?.price || 0) * pilgrims;
    }
    if (toggles.visa && selections.visa_id) {
      const visa = visas.find(v => v.id === selections.visa_id);
      total += (visa?.price || 0) * pilgrims;
    }
    if (toggles.hotel && selections.hotel_id) {
      const hotel = hotels.find(h => h.id === selections.hotel_id);
      total += (hotel?.price_per_night || 0) * pilgrims * nights;
    }
    if (toggles.transport && selections.transport_id) {
      const transport = transports.find(t => t.id === selections.transport_id);
      total += (transport?.price_per_trip || 0);
    }
    if (toggles.muthowif && selections.muthowif_id) {
      const muthowif = muthowifs.find(m => m.id === selections.muthowif_id);
      total += (muthowif?.price_per_group || 0);
    }

    return total;
  };

  const handleAddToCart = async () => {
    try {
      const customPackage = {
        package_type: 'umrah_mandiri',
        departure_date: selections.departure_date,
        return_date: selections.return_date,
        departure_city: selections.departure_city,
        total_pilgrims: selections.total_pilgrims,
        airline_id: toggles.airline ? selections.airline_id : null,
        visa_id: toggles.visa ? selections.visa_id : null,
        hotel_id: toggles.hotel ? selections.hotel_id : null,
        transport_id: toggles.transport ? selections.transport_id : null,
        muthowif_id: toggles.muthowif ? selections.muthowif_id : null,
        price: calculateTotal()
      };

      await axios.post(`${API}/cart/add`, {
        package_id: 'custom',
        quantity: selections.total_pilgrims,
        custom_package: customPackage
      });

      toast.success('Paket berhasil ditambahkan ke keranjang!');
      navigate('/cart');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Add to cart error:', error);
      }
      toast.error('Gagal menambahkan ke keranjang: ' + (error.response?.data?.detail || 'Silakan coba lagi'));
    }
  };

  // Helper: step toggle bar + choice content
  const renderStepToggle = (toggleKey, label, children) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
        <span className="font-medium">{label}</span>
        <button
          type="button"
          onClick={() => toggleStep(toggleKey)}
          className="text-primary hover:text-primary/80 transition-colors"
        >
          {toggles[toggleKey] ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
        </button>
      </div>
      {toggles[toggleKey] ? (
        children
      ) : (
        <p className="text-muted-foreground text-center py-6">Fasilitas ini tidak dipilih</p>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Tanggal Keberangkatan *</Label>
                <Input
                  type="date"
                  value={selections.departure_date}
                  onChange={(e) => setSelections({...selections, departure_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>Tanggal Pulang *</Label>
                <Input
                  type="date"
                  value={selections.return_date}
                  onChange={(e) => setSelections({...selections, return_date: e.target.value})}
                  min={selections.departure_date || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Jumlah Jamaah *</Label>
                <Input
                  type="number"
                  min="1"
                  value={selections.total_pilgrims}
                  onChange={(e) => setSelections({...selections, total_pilgrims: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Kota Keberangkatan</Label>
              <select
                className="w-full p-2 border border-input rounded-md"
                value={selections.departure_city}
                onChange={(e) => setSelections({...selections, departure_city: e.target.value})}
              >
                <option>Jakarta</option>
                <option>Surabaya</option>
                <option>Medan</option>
                <option>Makassar</option>
                <option>Bandung</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return renderStepToggle('airline', 'Maskapai Penerbangan',
          <div className="grid grid-cols-1 gap-4">
            {airlines.map((airline) => (
              <Card
                key={airline.id}
                className={`cursor-pointer transition-all ${
                  selections.airline_id === airline.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelections({...selections, airline_id: airline.id})}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{airline.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Kode: {airline.code} • Bagasi: {airline.baggage_allowance} kg
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        Rp {airline.price?.toLocaleString('id-ID')}
                      </p>
                      <p className="text-xs text-muted-foreground">per orang</p>
                    </div>
                    {selections.airline_id === airline.id && (
                      <Check className="h-6 w-6 text-primary ml-4" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 3:
        return renderStepToggle('visa', 'Visa',
          <div className="grid grid-cols-1 gap-4">
            {visas.map((visa) => (
              <Card
                key={visa.id}
                className={`cursor-pointer transition-all ${
                  selections.visa_id === visa.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelections({...selections, visa_id: visa.id})}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{visa.visa_type}</h3>
                      <p className="text-sm text-muted-foreground">
                        Berlaku: {visa.validity_days} hari
                      </p>
                      {visa.includes_siskopatuh && (
                        <Badge variant="default" className="mt-2">Termasuk Siskopatuh</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        Rp {visa.price?.toLocaleString('id-ID')}
                      </p>
                      <p className="text-xs text-muted-foreground">per orang</p>
                    </div>
                    {selections.visa_id === visa.id && (
                      <Check className="h-6 w-6 text-primary ml-4" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 4:
        return renderStepToggle('hotel', 'Hotel',
          <div className="grid grid-cols-1 gap-4">
            {hotels.map((hotel) => (
              <Card
                key={hotel.id}
                className={`cursor-pointer transition-all ${
                  selections.hotel_id === hotel.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelections({...selections, hotel_id: hotel.id})}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{hotel.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {hotel.city} • Jarak Haram: {hotel.distance_to_haram}m
                      </p>
                      <Badge variant="secondary" className="mt-2">
                        {hotel.rating} ⭐
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        Rp {hotel.price_per_night?.toLocaleString('id-ID')}
                      </p>
                      <p className="text-xs text-muted-foreground">per malam</p>
                    </div>
                    {selections.hotel_id === hotel.id && (
                      <Check className="h-6 w-6 text-primary ml-4" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 5:
        return renderStepToggle('transport', 'Transportasi',
          <div className="grid grid-cols-1 gap-4">
            {transports.map((transport) => (
              <Card
                key={transport.id}
                className={`cursor-pointer transition-all ${
                  selections.transport_id === transport.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelections({...selections, transport_id: transport.id})}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{transport.vehicle_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Kapasitas: {transport.capacity} orang
                      </p>
                      <Badge className="mt-2">{transport.vehicle_type}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        Rp {transport.price_per_trip?.toLocaleString('id-ID')}
                      </p>
                      <p className="text-xs text-muted-foreground">per hari</p>
                    </div>
                    {selections.transport_id === transport.id && (
                      <Check className="h-6 w-6 text-primary ml-4" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 6:
        return renderStepToggle('muthowif', 'Muthowif (Pembimbing)',
          <div className="grid grid-cols-1 gap-4">
            {muthowifs.map((muthowif) => (
              <Card
                key={muthowif.id}
                className={`cursor-pointer transition-all ${
                  selections.muthowif_id === muthowif.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelections({...selections, muthowif_id: muthowif.id})}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{muthowif.full_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Bahasa: {muthowif.languages?.join(', ')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Pengalaman: {muthowif.years_of_experience} tahun
                      </p>
                      <Badge variant="secondary" className="mt-2">
                        {muthowif.rating} ⭐
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        Rp {muthowif.price_per_group?.toLocaleString('id-ID')}
                      </p>
                      <p className="text-xs text-muted-foreground">per grup</p>
                    </div>
                    {selections.muthowif_id === muthowif.id && (
                      <Check className="h-6 w-6 text-primary ml-4" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 7:
        const selectedAirline = airlines.find(a => a.id === selections.airline_id);
        const selectedVisa = visas.find(v => v.id === selections.visa_id);
        const selectedHotel = hotels.find(h => h.id === selections.hotel_id);
        const selectedTransport = transports.find(t => t.id === selections.transport_id);
        const selectedMuthowif = muthowifs.find(m => m.id === selections.muthowif_id);
        const total = calculateTotal();
        const tax = settings.ppn_enabled ? total * (settings.ppn_rate / 100) : 0;

        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">Ringkasan Paket Umrah Mandiri Anda</h3>

            <Card>
              <CardHeader>
                <CardTitle>Detail Perjalanan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tanggal Keberangkatan</span>
                  <span className="font-medium">{selections.departure_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tanggal Pulang</span>
                  <span className="font-medium">{selections.return_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Jumlah Jamaah</span>
                  <span className="font-medium">{selections.total_pilgrims} orang</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Kota Keberangkatan</span>
                  <span className="font-medium">{selections.departure_city}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pilihan Fasilitas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Maskapai</span>
                  <span className="font-medium">{toggles.airline ? (selectedAirline?.name || '-') : 'Tidak dipilih'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Visa</span>
                  <span className="font-medium">{toggles.visa ? (selectedVisa?.visa_type || '-') : 'Tidak dipilih'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hotel</span>
                  <span className="font-medium">{toggles.hotel ? (selectedHotel?.name || '-') : 'Tidak dipilih'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transportasi</span>
                  <span className="font-medium">{toggles.transport ? (selectedTransport?.vehicle_name || '-') : 'Tidak dipilih'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Muthowif</span>
                  <span className="font-medium">{toggles.muthowif ? (selectedMuthowif?.full_name || '-') : 'Tidak dipilih'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rincian Biaya</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>Rp {total.toLocaleString('id-ID')}</span>
                </div>
                  {settings.ppn_enabled && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">PPN ({settings.ppn_rate}%)</span>
                  <span>Rp {tax.toLocaleString('id-ID')}</span>
                </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-4">
                  <span>Total</span>
                  <span>Rp {(total + tax).toLocaleString('id-ID')}</span>
                </div>
              </CardContent>
            </Card>

            <Button className="w-full" size="lg" onClick={handleAddToCart}>
              Tambahkan ke Keranjang — Rp {(total + tax).toLocaleString('id-ID')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 font-['Outfit']">Buat Paket Umrah Mandiri</h1>
          <p className="text-muted-foreground">Pilih sendiri fasilitas perjalanan Umrah Anda sesuai kebutuhan</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto">
          {steps.map((step) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex flex-col items-center ${
                currentStep >= step.number ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                  currentStep >= step.number
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted-foreground'
                }`}>
                  {currentStep > step.number ? <Check className="h-5 w-5" /> : step.number}
                </div>
                <p className="text-xs mt-2 text-center whitespace-nowrap hidden md:block">{step.title}</p>
              </div>
              {step.number < steps.length && (
                <div className={`w-8 md:w-16 h-0.5 mx-2 ${
                  currentStep > step.number ? 'bg-primary' : 'bg-muted-foreground/30'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="p-8">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Sebelumnya
          </Button>
          {currentStep < steps.length ? (
            <Button onClick={handleNext}>
              Selanjutnya
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UmrahMandiriPage;